import React from "react";
import { useLocation } from "react-router-dom";
import { intl } from "dmsbasereact";
import { ReportLayout } from "./components/ReportLayout";
import { formatCpf, formatCep } from "../../utils/formatters";

export const PatientsReportPage = () => {
    const location = useLocation();
    const initialFilters = location.state?.initialFilters || null;
    // Column order matching .ai_docs/reports.md
    const header = [
        {
            label: intl.get("Reports.Patients.Name").d("Patient Name"),
            column_name: "name",
            width: "180px",
        },
        {
            label: intl.get("Reports.Patients.PatientNumber").d("Patient Number"),
            column_name: "patient_identifier",
            width: "130px",
        },
        {
            label: intl.get("BirthDate").d("Birth Date"),
            column_name: "birth_date",
            width: "100px",
            function: (value) => (value ? new Date(value).toLocaleDateString("pt-BR") : "--"),
        },
        {
            label: intl.get("CPF").d("CPF"),
            column_name: "cpf",
            width: "130px",
            function: (value) => formatCpf(value) || "--",
        },
        {
            label: intl.get("Email").d("Email"),
            column_name: "email",
            width: "180px",
        },
        {
            label: intl.get("Phone").d("Phone"),
            column_name: "phone",
            width: "130px",
        },
        {
            label: intl.get("Reports.Patients.RegistrationDate").d("Registration Date"),
            column_name: "registration_date",
            width: "110px",
            function: (value) => (value ? new Date(value).toLocaleDateString("pt-BR") : "--"),
        },
        {
            label: intl.get("Reports.Patients.RiskCategory").d("Risk Category"),
            column_name: "risk_category",
            width: "180px",
        },
        {
            label: intl.get("Reports.Patients.InfertilityConfirmedBy").d("Infertility Confirmed By"),
            column_name: "infertility_confirmed_by",
            width: "180px",
        },
        {
            label: intl.get("Reports.Patients.EffectiveContraceptiveMethods").d("Effective Contraceptive Methods"),
            column_name: "effective_contraceptive_methods",
            width: "200px",
        },
        {
            label: intl.get("Reports.Patients.BarrierContraceptiveMethods").d("Barrier Contraceptive Methods"),
            column_name: "barrier_contraceptive_methods",
            width: "180px",
        },
        {
            label: intl.get("Address").d("Address"),
            column_name: "address",
            width: "180px",
        },
        {
            label: intl.get("City").d("City"),
            column_name: "city",
            width: "120px",
        },
        {
            label: intl.get("State").d("State"),
            column_name: "state",
            width: "60px",
        },
        {
            label: intl.get("ZipCode").d("Zip Code"),
            column_name: "zip_code",
            width: "90px",
            function: (value) => formatCep(value) || "--",
        },
        {
            label: intl.get("HasLegalGuardian").d("Legal Guardian"),
            column_name: "has_legal_guardian",
            width: "90px",
        },
        {
            label: intl.get("LegalGuardianName").d("Guardian Name"),
            column_name: "legal_guardian_name",
            width: "160px",
        },
        {
            label: intl.get("LegalGuardianCPF").d("Guardian CPF"),
            column_name: "legal_guardian_cpf",
            width: "130px",
            function: (value) => formatCpf(value) || "--",
        },
        {
            label: intl.get("LegalGuardianPhone").d("Guardian Phone"),
            column_name: "legal_guardian_phone",
            width: "120px",
        },
        {
            label: intl.get("Reports.Patients.LegalGuardianAddress").d("Guardian Address"),
            column_name: "legal_guardian_address",
            width: "160px",
        },
        {
            label: intl.get("Reports.Patients.LegalGuardianCity").d("Guardian City"),
            column_name: "legal_guardian_city",
            width: "120px",
        },
        {
            label: intl.get("Reports.Patients.LegalGuardianState").d("Guardian State"),
            column_name: "legal_guardian_state",
            width: "60px",
        },
        {
            label: intl.get("Reports.Patients.LegalGuardianZip").d("Guardian Zip"),
            column_name: "legal_guardian_zip",
            width: "90px",
            function: (value) => formatCep(value) || "--",
        },
        {
            label: intl.get("TotalPrescriptions").d("Total Prescriptions"),
            column_name: "total_prescriptions",
            width: "90px",
        },
        {
            label: intl.get("TotalDispensations").d("Total Dispensations"),
            column_name: "total_dispensations",
            width: "100px",
        },
        {
            label: intl.get("Reports.Patients.LastPrescription").d("Last Prescription"),
            column_name: "last_prescription",
            width: "100px",
            function: (value) => (value ? new Date(value).toLocaleDateString("pt-BR") : "--"),
        },
        {
            label: intl.get("LastDispensation").d("Last Dispensation"),
            column_name: "last_dispensation",
            width: "100px",
            function: (value) => (value ? new Date(value).toLocaleDateString("pt-BR") : "--"),
        },
        {
            label: intl.get("Reports.Patients.LinkedDoctors").d("Linked Doctors"),
            column_name: "linked_doctors",
            width: "180px",
        },
        {
            label: intl.get("Status").d("Status"),
            column_name: "status",
            width: "80px",
            function: (value) => intl.get(`PatientStatus.${value}`).d(value),
        },
        {
            label: intl.get("Reports.Patients.StatusChangeReason").d("Status Change Reason"),
            column_name: "status_change_reason",
            width: "150px",
            function: (value) => (value ? intl.get(value).d(value) : value),
        },
        {
            label: intl.get("Reports.Patients.StatusChangeJustification").d("Status Change Justification"),
            column_name: "status_change_justification",
            width: "200px",
        },
        {
            label: intl.get("Reports.Patients.StatusChangeDate").d("Status Change Date"),
            column_name: "status_change_date",
            width: "110px",
            function: (value) => (value ? new Date(value).toLocaleDateString("pt-BR") : "--"),
        },
        {
            label: intl.get("Reports.Patients.StatusChangedBy").d("Status Changed By"),
            column_name: "status_changed_by",
            width: "150px",
        },
    ];

    const filterFields = [
        { name: "name", label: intl.get("Name").d("Name"), type: "text" },
        { name: "cpf", label: intl.get("CPF").d("CPF"), type: "text" },
        {
            name: "risk_category",
            label: intl.get("Reports.Patients.RiskCategory").d("Risk Category"),
            type: "multiselect",
            optionsKey: "risk_category",
        },
        { name: "city", label: intl.get("City").d("City"), type: "text" },
        {
            name: "has_legal_guardian",
            label: intl.get("HasLegalGuardian").d("Legal Guardian"),
            type: "select",
            options: [
                { value: "yes", label: intl.get("Yes").d("Yes") },
                { value: "no", label: intl.get("No").d("No") },
            ],
        },
        { name: "status", label: intl.get("Status").d("Status"), type: "multiselect" },
    ];

    return (
        <ReportLayout
            title="Reports.Patients.PageTitle"
            titleDefault="Patients Report"
            manualSlug="reports-patients"
            tableUrl="/reports/patients/table"
            filterOptionsUrl="/reports/patients/filter-options"
            exportUrl="/reports/patients/export"
            header={header}
            filterFields={filterFields}
            dateFilterLabel={intl.get("Reports.DateFilter.RegistrationDate").d("Registration Date")}
            initialFilters={initialFilters}
        />
    );
};

export default PatientsReportPage;
